
import Vue from "vue";
import Btn from "@/components/v2/base/Btn.vue";
import BottomSheet from "@/components/base/BottomSheet.vue";

export default Vue.extend({
  name: "LessonIntroOverlay",
  components: { Btn, BottomSheet },
  props: {
    introMarkdown: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isTyping: false,
      showButton: false,
      typewriterTimer: null as ReturnType<typeof setInterval> | null,
      allChars: [] as HTMLElement[],
      charIdx: 0,
      cursorEl: null as HTMLElement | null,
    };
  },
  mounted() {
    this.startTypewriter();
  },
  beforeDestroy() {
    if (this.typewriterTimer) {
      clearInterval(this.typewriterTimer);
    }
  },
  methods: {
    renderMarkdown(md: string): string {
      return md
        .replace(/^## (.+)$/gm, '<h2 class="lesson-intro-title">$1</h2>')
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/~~(.+?)~~/g, "<del>$1</del>")
        .replace(/\*([^*]+?)\*/g, '<em class="lesson-intro-em">$1</em>')
        .split("\n\n")
        .map((b: string) => {
          b = b.trim();
          if (!b) return "";
          if (b.startsWith("<h2")) return b;
          if (b.includes("Wskazówka:") || b.includes("wskazówka:"))
            return '<div class="lesson-intro-hint">' + b + "</div>";
          // Render sentences separated by · as a list
          if (b.includes(" · ")) {
            const items = b.split(" · ").map((s: string) => `<li>${s.trim()}</li>`);
            return '<ul class="lesson-intro-sentences">' + items.join("") + "</ul>";
          }
          return "<p>" + b + "</p>";
        })
        .join("");
    },

    startTypewriter() {
      const container = this.$refs.introContent as HTMLElement;
      if (!container) return;

      container.innerHTML = this.renderMarkdown(this.introMarkdown);
      this.isTyping = true;

      // Hide containers that have backgrounds until their text appears
      container.querySelectorAll(".lesson-intro-hint, .lesson-intro-sentences").forEach((el) => {
        (el as HTMLElement).style.opacity = "0";
      });

      const elements = Array.from(container.querySelectorAll("h2, p, .lesson-intro-hint, li"));
      this.allChars = [];

      elements.forEach((el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        const textNodes: Text[] = [];
        while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

        textNodes.forEach((node) => {
          const text = node.textContent || "";
          const frag = document.createDocumentFragment();
          for (let i = 0; i < text.length; i++) {
            const span = document.createElement("span");
            span.className = "typewriter-char";
            span.textContent = text[i];
            frag.appendChild(span);
            this.allChars.push(span);
          }
          node.parentNode?.replaceChild(frag, node);
        });
      });

      // Create inline cursor
      this.cursorEl = document.createElement("span");
      this.cursorEl.className = "lesson-intro-cursor";

      const speed = 75;
      const charsPerTick = 4;

      this.typewriterTimer = setInterval(() => {
        for (let j = 0; j < charsPerTick && this.charIdx < this.allChars.length; j++) {
          const charEl = this.allChars[this.charIdx];
          charEl.classList.add("visible");
          // Reveal parent container (hint/list) when first char appears
          const parent = charEl.closest(".lesson-intro-hint, .lesson-intro-sentences");
          if (parent && (parent as HTMLElement).style.opacity === "0") {
            (parent as HTMLElement).style.opacity = "1";
          }
          // Move cursor after the last revealed char
          charEl.after(this.cursorEl as HTMLElement);
          this.charIdx++;
        }

        if (this.charIdx >= this.allChars.length) {
          this.finishTyping();
        }
      }, speed);
    },

    skipToEnd() {
      if (!this.isTyping) return;
      const container = this.$refs.introContent as HTMLElement;
      container?.querySelectorAll(".lesson-intro-hint, .lesson-intro-sentences").forEach((el) => {
        (el as HTMLElement).style.opacity = "1";
      });
      for (let i = this.charIdx; i < this.allChars.length; i++) {
        this.allChars[i].classList.add("visible");
      }
      this.charIdx = this.allChars.length;
      this.finishTyping();
    },

    finishTyping() {
      if (this.typewriterTimer) {
        clearInterval(this.typewriterTimer);
        this.typewriterTimer = null;
      }
      if (this.cursorEl) {
        this.cursorEl.remove();
        this.cursorEl = null;
      }
      this.isTyping = false;
      this.showButton = true;
    },
  },
});
